import { useEffect, useRef, useState, type ReactNode } from 'react';

type Props = {
  children: ReactNode;
  /** Animation direction. */
  direction?: 'up' | 'left' | 'right';
  /** Delay in ms (rounded to 100, 200, 300, 400, 500). */
  delay?: 0 | 100 | 200 | 300 | 400 | 500;
  /** Render as a different element (default 'div'). */
  as?: 'div' | 'section' | 'span' | 'article';
  className?: string;
};

/**
 * Reveal wraps children in an Intersection-Observer-driven fade/slide
 * animation. Triggers once when 15% of the element is visible.
 */
export default function Reveal({
  children,
  direction = 'up',
  delay = 0,
  as: Tag = 'div',
  className = '',
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`reveal ${visible ? 'is-visible' : ''} ${className}`.trim()}
      data-reveal={direction === 'up' ? undefined : direction}
      data-reveal-delay={delay || undefined}
    >
      {children}
    </Tag>
  );
}
